import { Cell, Grid, Heading, Modal, ModalBody, Tabs, Theme, useStyles } from 'bold-ui'
import { usePecField } from 'components/form/final-form/useField'
import { PageLoading } from 'components/loading'
import { PecSwitch, TabLink } from 'components/route'
import { useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import moment from 'moment'
import React from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { Redirect, Route } from 'react-router-dom'
import { calcularIMC } from 'util/atendimento'
import { MetaArray } from 'util/metaPath'
import { formatNumber, parseNumber } from 'util/number'
import { MedicoesPanelModel } from 'view/atendimentos/components/MedicoesPanel/MedicoesPanel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/HistoricoMedicaoModel'

import { HistoricoMedicoesAntropometriaView } from './HistoricoMedicoesAntropometriaView'
import { HistoricoMedicoesGlicemiaView } from './HistoricoMedicoesGlicemiaView'
import { HistoricoMedicoesSinaisVitaisView } from './HistoricoMedicoesSinaisVitaisView'
import { MedicaoAnteriorFormModel } from './MedicaoAnteriorForm'

export interface HistoricoMedicoesModalProps {
  cidadao: CidadaoAtendimento
  prontuarioId: ID
  name: MetaArray<MedicaoAnteriorFormModel>
  medicoesAtendimento: MedicoesPanelModel
  dataAtendimento: Instant
}

export function HistoricoMedicoesModal(props: HistoricoMedicoesModalProps) {
  const { cidadao, dataAtendimento, name, prontuarioId, medicoesAtendimento } = props
  const history = useHistory()
  const match = useRouteMatch()
  const formField = usePecField({ name })
  const medicoesAnteriores: MedicaoAnteriorFormModel[] = formField.input.value ?? []

  const onAddMedicaoAnterior = (medicao: MedicaoAnteriorFormModel) => {
    formField.input.onChange([medicao, ...formField.input.value])
    formField.input.onBlur()
  }

  const onRemoveMedicaoAnterior = (id: ID) => {
    formField.input.onChange([...formField.input.value?.filter((m: MedicaoAnteriorFormModel) => m.id !== id)])
    formField.input.onBlur()
  }

  const { classes } = useStyles(createStyles)

  const { data, loading } = useMedicoesAtendimentosAnterioresQuery({
    variables: { input: { prontuarioId: prontuarioId } },
  })

  const medicoes = (data?.medicoes?.content ?? []).sort((a, b) => b.dataMedicao - a.dataMedicao)

  const medicoesExibir: HistoricoMedicaoModel[] = [
    ...(medicoesAnteriores?.map(convertMedicaoAnterior) ?? []),
    ...(medicoesAtendimento ? [convertMedicaoAtendimento(medicoesAtendimento, dataAtendimento)] : []),
    ...medicoes.map(convertMedicaoOutrosAtendimentos),
  ]

  const onClose = () => {
    return history.push(match.url.replace('/historicoMedicoes', ''))
  }

  return (
    <Modal open={true} onClose={onClose}>
      <ModalBody>
        <Grid alignItems='flex-start' gap={0}>
          <Cell size={12}>
            <Heading level={1} className={classes.header}>
              Histórico de medições
            </Heading>
          </Cell>
          <Cell size={12}>
            <Tabs>
              <TabLink to={`${match.url}/antropometria`}>Antropometria</TabLink>
              <TabLink to={`${match.url}/sinaisVitais`}>Sinais vitais</TabLink>
              <TabLink to={`${match.url}/glicemia`}>Glicemia</TabLink>
            </Tabs>
          </Cell>
          <Cell size={12}>
            {loading && <PageLoading message='Carregando dados' />}
            {!loading && (
              <PecSwitch>
                <Redirect exact path={match.url} to={`${match.url}/antropometria`} />
                <Route
                  path={`${match.url}/antropometria`}
                  render={(renderProps) => (
                    <HistoricoMedicoesAntropometriaView
                      {...renderProps}
                      medicoes={medicoesExibir}
                      cidadao={cidadao}
                      dataAtendimento={dataAtendimento}
                      onAddMedicaoAnterior={onAddMedicaoAnterior}
                      onRemoveMedicaoAnterior={onRemoveMedicaoAnterior}
                    />
                  )}
                />
                <Route
                  path={`${match.url}/sinaisVitais`}
                  render={(renderProps) => (
                    <HistoricoMedicoesSinaisVitaisView
                      {...renderProps}
                      medicoes={medicoesExibir}
                      gestacoes={cidadao?.gestacoes ?? []}
                    />
                  )}
                />
                <Route
                  path={`${match.url}/glicemia`}
                  render={(renderProps) => (
                    <HistoricoMedicoesGlicemiaView
                      {...renderProps}
                      medicoes={medicoesExibir}
                      gestacoes={cidadao?.gestacoes ?? []}
                    />
                  )}
                />
              </PecSwitch>
            )}
          </Cell>
        </Grid>
      </ModalBody>
    </Modal>
  )
}

const createStyles = (theme: Theme) => ({
  header: {
    marginBottom: '1rem',
  },
})

function convertMedicaoAnterior(medicaoAnterior: MedicaoAnteriorFormModel): HistoricoMedicaoModel {
  const imc =
    medicaoAnterior.peso &&
    medicaoAnterior.altura &&
    calcularIMC(parseNumber(medicaoAnterior.peso), parseNumber(medicaoAnterior.altura))
  return {
    id: medicaoAnterior.id,
    dataMedicao: moment(medicaoAnterior.data),
    valorPeso: medicaoAnterior.peso && formatNumber(parseNumber(medicaoAnterior.peso)),
    valorAltura: medicaoAnterior.altura && formatNumber(parseNumber(medicaoAnterior.altura)),
    valorPerimetroCefalico:
      medicaoAnterior.perimetroCefalico && formatNumber(parseNumber(medicaoAnterior.perimetroCefalico)),
    valorImc: imc && formatNumber(imc),
    tipoMedicao: 'anterior',
  }
}

function convertMedicaoOutrosAtendimentos(medicao: any): HistoricoMedicaoModel {
  return {
    ...medicao,
    dataRegistro: medicao.atendimentoProfissional.iniciadoEm,
    nomeResponsavel: medicao.atendimentoProfissional.atendimento.lotacao.profissional.nome,
  }
}

function convertMedicaoAtendimento(
  medicaoAtendimento: MedicoesPanelModel,
  dataAtendimento: Instant
): HistoricoMedicaoModel {
  const imc =
    medicaoAtendimento.peso &&
    medicaoAtendimento.altura &&
    calcularIMC(parseNumber(medicaoAtendimento.peso), parseNumber(medicaoAtendimento.altura))

  return {
    dataMedicao: dataAtendimento,
    valorPeso: medicaoAtendimento.peso && formatNumber(medicaoAtendimento.peso),
    valorAltura: medicaoAtendimento.altura && formatNumber(medicaoAtendimento.altura),
    valorPerimetroCefalico: medicaoAtendimento.perimetroCefalico && formatNumber(medicaoAtendimento.perimetroCefalico),
    valorCircunferenciaAbdominal:
      medicaoAtendimento.circunferenciaAbdominal && formatNumber(medicaoAtendimento.circunferenciaAbdominal),
    perimetroPanturrilha:
      medicaoAtendimento.perimetroPanturrilha && formatNumber(medicaoAtendimento.perimetroPanturrilha),
    valorImc: imc && formatNumber(imc),
    tipoGlicemia: {
      tipoGlicemiaDbEnum: medicaoAtendimento.tipoGlicemia,
    },
    valorGlicemia: medicaoAtendimento.glicemia,
    valorFrequenciaCardiaca: medicaoAtendimento.frequenciaCardiaca,
    valorFrequenciaRespiratoria: medicaoAtendimento.frequenciaRespiratoria,
    valorPressaoArterial:
      medicaoAtendimento.pressaoArterialSistolica && medicaoAtendimento.pressaoArterialDiastolica
        ? `${medicaoAtendimento.pressaoArterialSistolica}/${medicaoAtendimento.pressaoArterialDiastolica}`
        : undefined,
    valorSaturacaoO2: medicaoAtendimento.saturacaoO2,
    valorTemperatura: medicaoAtendimento.temperatura && formatNumber(medicaoAtendimento.temperatura, 1),
    tipoMedicao: 'atual',
  }
}
